import React from 'react'
import styled from '@emotion/styled'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { colors, radius, font, shadow, spacing } from '@mobi/component-library/Foundations'
import { Button } from '@mobi/component-library/Base/Buttons'
import { toMoney } from '@mobi/utils/money'
import { dayjs } from '@mobi/utils/date'
import { useFeature } from '@core/Utils/hooks'
import { FeatureFlags } from '@mobi/settings/featureFlags'

export interface BonusBetOrCashItemModel {
  mode: 'BonusCash' | 'BonusBet'
  remainingAmount: number
  expiry: Date
}

interface BonusBetOrCashItemProps {
  model: BonusBetOrCashItemModel
  canBeSplit?: boolean
  betsRemaining?: number
  openSplitBonusBetTray?: () => void
}

export const BonusBetOrCashItem = ({
  model,
  canBeSplit,
  betsRemaining,
  openSplitBonusBetTray,
}: BonusBetOrCashItemProps): JSX.Element | null => {
  const splitBonusBets = useFeature(FeatureFlags.SPLIT_BONUS_BETS.key)

  const hasTwoInStack = betsRemaining && betsRemaining > 1
  const hasThreeInStack = betsRemaining && betsRemaining > 2

  return (
    <WrapperStyled
      className={hasThreeInStack ? 'spacing-three' : hasTwoInStack ? 'spacing-two' : ''}
    >
      <div>
        <div>
          <div>
            <Icon name='LineCurrencyDollarCircle' color={colors.success[600]} size='1.6rem' />
          </div>
          <div>
            <span>{model.mode === 'BonusCash' ? 'Bonus Cash' : 'Bonus Bet'}</span>
            <span>Expires in {dayjs(model.expiry).fromNow(true)}</span>
          </div>
          <div>
            <span>
              {toMoney(model.remainingAmount)}
              {model.mode === 'BonusCash' ? ' Remaining' : ''}
            </span>
            {hasTwoInStack ? <span>{betsRemaining} Bets Remaining</span> : null}
          </div>
        </div>
        {model.mode === 'BonusBet' && splitBonusBets && canBeSplit ? (
          <div>
            <Button
              color='secondary_color'
              isFullWidth={true}
              size='sm'
              onClick={openSplitBonusBetTray}
            >
              Split Bonus Bet
            </Button>
          </div>
        ) : null}
      </div>
      {hasTwoInStack ? <div /> : null}
      {hasThreeInStack ? <div /> : null}
    </WrapperStyled>
  )
}

const WrapperStyled = styled.div({
  position: 'relative',
  margin: `${spacing.smx2} ${spacing.sm}`,

  '&.spacing-two': {
    marginBottom: spacing.smx1,
  },

  '&.spacing-three': {
    marginBottom: spacing.sm,
  },

  '> div': {
    backgroundColor: colors.white,
    border: `0.1rem solid ${colors.neutral[200]}`,
    borderRadius: radius.lgx1,
    userSelect: 'none',
  },

  '> div:first-of-type': {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.smx1,
    fontFamily: font.family.primary,
    fontSize: font.size.lg.fontSize,
    lineHeight: font.size.lg.lineHeight,
    padding: spacing.sm,
    boxShadow: shadow.xs,
    zIndex: 3,

    span: {
      display: 'block',
    },

    '> div:first-of-type': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: spacing.smx1,

      '> div:nth-of-type(1)': {
        display: 'flex',
        alignItems: 'center',
        height: spacing.bigx3,
        width: spacing.bigx3,
        backgroundColor: colors.celery[50],
        borderRadius: radius.full,

        'span:first-of-type': {
          marginLeft: spacing.smx1,
        },
      },

      '> div:nth-of-type(2) > span:nth-of-type(1)': {
        fontWeight: font.weight.medium,
        color: colors.black,
      },

      '> div:nth-of-type(2) > span:nth-of-type(2)': {
        fontWeight: font.weight.regular,
        fontSize: font.size.md.fontSize,
        lineHeight: font.size.md.lineHeight,
        color: colors.neutral[800],
      },

      '> div:nth-of-type(3)': {
        marginLeft: 'auto',

        '> span:nth-of-type(1)': {
          fontWeight: font.weight.semibold,
          color: colors.black,
          textAlign: 'end',
        },

        '> span:nth-of-type(2)': {
          backgroundColor: colors.neutral[200],
          color: colors.neutral[900],
          fontSize: font.size.base.fontSize,
          fontWeight: font.weight.medium,
          lineHeight: font.size.base.lineHeight,
          borderRadius: radius.sm,
          padding: '0.2rem 0.4rem',
        },
      },
    },
  },

  '> div:nth-of-type(2), > div:nth-of-type(3)': {
    position: 'absolute',
    height: spacing.bigx2,
    left: '50%',
    transform: 'translateX(-50%)',
    boxShadow: shadow.xs,
  },

  '> div:nth-of-type(2)': {
    width: `calc(100% - ${spacing.md})`,
    bottom: `-${spacing.smx2}`,
    zIndex: 1,
  },

  '> div:nth-of-type(3)': {
    width: `calc(100% - ${spacing.bigx3})`,
    bottom: `-${spacing.smx1}`,
    zIndex: 0,
  },
})
